import { useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SwipeableTabs } from "@/components/ui/swipeable-tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildPppcPolicyActions } from "@/components/actions";
import { toast } from "sonner";
import { useAllPppcPolicies, useDeletePppcPolicy } from "@/hooks/usePppcPolicy";
import { PppcActivityLog } from "@/components/applications/pppc/PppcActivityLog";
import { PPPC_SERVICE_GROUPS } from "@/types/pppc";

export default function PppcPolicyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: policies = [], isLoading } = useAllPppcPolicies();
  const deleteMutation = useDeletePppcPolicy();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pppcTab, setPppcTab] = useState("overview");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const policy = policies.find((p) => p.policyId === id);
  if (!policy) return <Navigate to="/configurations/pppc" replace />;

  const choiceCount = Object.values(policy.choices ?? {}).filter(
    (c) => c === "allow" || c === "deny",
  ).length;
  const allowCount = Object.values(policy.choices ?? {}).filter((c) => c === "allow")
    .length;
  const denyCount = Object.values(policy.choices ?? {}).filter((c) => c === "deny")
    .length;

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6 space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/configurations/pppc")}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to PPPC Library
          </Button>
          <PageHeader
            title={policy.appName || policy.policyName || "PPPC Policy"}
            description={`${policy.identifierType === "path" ? "Path" : "Bundle ID"}: ${policy.identifier}`}
          >
            <ActionsDropdown
              align="end"
              actions={buildPppcPolicyActions({
                surface: "detail",
                onView: () => {},
                onEdit: () => navigate(`/configurations/pppc/${policy.policyId}/edit`),
                onDuplicate: () =>
                  navigate(`/configurations/pppc/new?duplicate=${policy.policyId}`),
                onDelete: () => setShowDeleteDialog(true),
              })}
            />
          </PageHeader>
        </div>

        <SwipeableTabs
          value={pppcTab}
          onValueChange={setPppcTab}
          items={[
            {
              value: "overview",
              label: "Overview",
              content: (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs text-muted-foreground">Services configured</p>
                        <p className="text-2xl font-semibold">{choiceCount}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs text-muted-foreground">Allowed</p>
                        <p className="text-2xl font-semibold text-success">{allowCount}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <p className="text-xs text-muted-foreground">Denied</p>
                        <p className="text-2xl font-semibold text-destructive">{denyCount}</p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        Configured services
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {choiceCount === 0 ? (
                        <p className="text-sm text-muted-foreground">No services configured yet.</p>
                      ) : (
                        <div className="space-y-4">
                          {PPPC_SERVICE_GROUPS.map((group) => {
                            const rows = group.services.filter((s) => {
                              const c = policy.choices?.[s.key];
                              return c === "allow" || c === "deny";
                            });
                            if (rows.length === 0) return null;
                            return (
                              <div key={group.groupLabel}>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                                  {group.groupLabel}
                                </p>
                                <div className="space-y-1">
                                  {rows.map((s) => {
                                    const c = policy.choices?.[s.key];
                                    return (
                                      <div
                                        key={s.key}
                                        className="flex items-center justify-between text-sm py-1"
                                      >
                                        <span>{s.label}</span>
                                        <Badge variant={c === "allow" ? "default" : "destructive"}>
                                          {c === "allow" ? "Allow" : "Deny"}
                                        </Badge>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Code Requirement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs font-mono bg-muted rounded p-3 whitespace-pre-wrap break-all">
                        {policy.codeRequirement || "—"}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              ),
            },
            {
              value: "activity",
              label: "Activity",
              content: <PppcActivityLog policyId={policy.policyId} />,
            },
          ]}
        />
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete PPPC Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "
              {policy.appName || policy.policyName || "this policy"}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await deleteMutation.mutateAsync(policy.policyId);
                  toast.success("PPPC Policy deleted");
                  navigate("/configurations/pppc");
                } catch {
                  toast.error("Failed to delete PPPC policy");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

